'use client';

import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

/**
 * Current user data structure
 */
export interface CurrentUser {
  id: string;
  email?: string;
}

/**
 * Hook to get the current authenticated user
 *
 * Uses TanStack Query to cache the user data and avoid
 * redundant auth.getUser() calls across the application.
 *
 * @returns Current user data and query state
 */
export function useCurrentUser() {
  const client = useSupabase();

  const query = useQuery({
    queryKey: ['current-user'],
    queryFn: async (): Promise<CurrentUser | null> => {
      const {
        data: { user },
      } = await client.auth.getUser();

      if (!user) {
        return null;
      }

      return {
        id: user.id,
        email: user.email,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - user rarely changes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false, // Don't refetch on focus
    refetchOnMount: false, // Use cached value
  });

  return {
    user: query.data ?? null,
    userId: query.data?.id ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
